<script lang="ts">
	// Renders the active imperative dialog (alert / confirm / prompt). Mount this
	// ONCE near the root of each app (it portals to the body, so placement in the
	// markup doesn't matter). Driven entirely by ./dialog-store.svelte.ts — call
	// `dialogs.confirm(...)` etc. from anywhere and this shows it.
	//
	// Built from the shared Dialog primitive + Button + Input so it stays on-theme
	// and dark-mode-correct across all three apps. NO raw HTML controls.
	import {
		Dialog,
		DialogContent,
		DialogHeader,
		DialogFooter,
		DialogTitle,
		DialogDescription,
	} from "../components/ui/dialog/index.js";
	import { Button } from "../components/ui/button/index.js";
	import { Input } from "../components/ui/input/index.js";
	import { dialogState, dialogActions } from "./dialog-store.svelte.js";

	// Shared Button/Input expose `ref` as the generic HTMLElement (WithElementRef
	// defaults to HTMLElement), so the bound vars match that type.
	let inputEl = $state<HTMLElement | null>(null);
	let confirmEl = $state<HTMLElement | null>(null);
	let cancelEl = $state<HTMLElement | null>(null);
	let promptValue = $state("");

	// Reset the prompt field whenever a new dialog becomes active.
	$effect(() => {
		promptValue = dialogState.current?.defaultValue ?? "";
	});

	// Focus the right control on open: the input for prompts, the Cancel button for
	// destructive confirms (so Enter doesn't fire the dangerous action), else the
	// confirm button.
	function onOpenAutoFocus(e: Event): void {
		e.preventDefault();
		const c = dialogState.current;
		queueMicrotask(() => {
			if (c?.kind === "prompt") {
				inputEl?.focus();
				(inputEl as HTMLInputElement | null)?.select();
			} else if (c?.tone === "danger") {
				cancelEl?.focus();
			} else {
				confirmEl?.focus();
			}
		});
	}
</script>

<Dialog
	open={dialogState.open}
	onOpenChange={(v) => {
		if (!v) dialogActions.cancel();
	}}
>
	{#if dialogState.current}
		{@const c = dialogState.current}
		<DialogContent {onOpenAutoFocus} showClose={c.kind !== "alert"}>
			<DialogHeader>
				<!-- Title is always present (labels the dialog for a11y). With no
				     explicit title the message becomes the prominent line; bits-ui
				     omits aria-describedby when there's no Description, so each piece
				     of text appears exactly once. -->
				<DialogTitle>{c.title || c.message || c.okLabel}</DialogTitle>
				{#if c.title && c.message}
					<DialogDescription>{c.message}</DialogDescription>
				{/if}
			</DialogHeader>

			{#if c.kind === "prompt"}
				<form
					onsubmit={(e) => {
						e.preventDefault();
						dialogActions.accept(promptValue);
					}}
				>
					<Input
						bind:ref={inputEl}
						bind:value={promptValue}
						type={c.inputType}
						placeholder={c.placeholder}
					/>
				</form>
			{/if}

			<DialogFooter>
				{#if c.cancelLabel}
					<Button bind:ref={cancelEl} variant="outline" onclick={() => dialogActions.cancel()}>
						{c.cancelLabel}
					</Button>
				{/if}
				<Button
					bind:ref={confirmEl}
					variant={c.tone === "danger" ? "destructive" : "primary"}
					onclick={() => dialogActions.accept(promptValue)}
				>
					{c.okLabel}
				</Button>
			</DialogFooter>
		</DialogContent>
	{/if}
</Dialog>
